export class ChatMessage {
  static fromApiMessage(message: any, chatUserId: string): ChatMessage {
    if (!message) return undefined
    return new ChatMessage(
      message._id,
      message.rid,
      message.msg,
      message.u.name,
      message.u._id,
      message.ts,
      message.t,
      message.u._id === chatUserId,
      message?.attachments?.map((a: any) => a.image_url)
    )
  }

  private readonly regex =
    /(?=\[(!\[.+?\]\(.+?\)|.+?)]\((https:\/\/[^\)]+)\))/gi
  sendingStatus?: ChatMessageSendingStatus = undefined

  get date(): Date {
    return new Date(this.timestamp)
  }

  constructor(
    readonly id: string,
    readonly roomId: string,
    readonly content: string,
    readonly sender: string,
    readonly senderId: string,
    readonly timestamp: string,
    readonly type?: ChatMessageType,
    readonly isUserMessage?: boolean,
    readonly images?: string[],
    sendingStatus?: ChatMessageSendingStatus
  ) {
    this.sendingStatus = sendingStatus
  }

  static fromJson(jsonMessage: ChatMessage): ChatMessage {
    // create new ChatMessage object from json
    return new ChatMessage(
      jsonMessage.id,
      jsonMessage.roomId,
      jsonMessage.content,
      jsonMessage.sender,
      jsonMessage.senderId,
      jsonMessage.timestamp,
      jsonMessage.type,
      jsonMessage.isUserMessage,
      jsonMessage.images,
      jsonMessage.sendingStatus
    )
  }

  getLinks(): { text: string; url: string }[] {
    let links = [...this.content.matchAll(this.regex)].map((m) => ({
      text: m[1],
      url: m[2],
    }))
    return links.length > 0 ? links : undefined
  }

  getStatusMessage(): string {
    if (!this.type) {
      return undefined
    }
    switch (this.type) {
      case ChatMessageType.roomNameChange:
        return 'Room name changed to ' + this.content
      case ChatMessageType.userAddedBy:
        return this.sender + ' added ' + this.content
      case ChatMessageType.userRemovedBy:
        return this.sender + ' removed ' + this.content
      case ChatMessageType.userLeft:
        return this.sender + ' left'
      case ChatMessageType.useLeftTeam:
        return this.sender + ' left the team'
      case ChatMessageType.userJoined:
        return this.sender + ' joined'
      case ChatMessageType.userJoinedTeam:
        return this.sender + ' joined the team'
      case ChatMessageType.userJoinedConversation:
        return this.sender + ' joined the conversation'
      case ChatMessageType.messageRemoved:
        return 'Message removed'
      case ChatMessageType.addedUserToTeam:
        return 'Added ' + this.content + ' to the team'
      case ChatMessageType.removedUserFromTeam:
        return 'Removed ' + this.content + ' from the team'
      case ChatMessageType.userMutedBy:
        return 'User muted by ' + this.sender
      case ChatMessageType.roleAdded:
        return 'Added ' + this.content + ' to the owners'
      default:
        return ''
    }
  }

  getContentType(): ChatMessageContentType {
    if (this.images?.length > 0) {
      return ChatMessageContentType.image
    } else if (this.getLinks()) {
      return ChatMessageContentType.link
    }
    return ChatMessageContentType.message
  }
}

/**
 * see https://developer.rocket.chat/v1/docs/message#message-type
 */
export enum ChatMessageType {
  roomNameChange = 'r',
  userAddedBy = 'au',
  userRemovedBy = 'ru',
  userLeft = 'ul',
  useLeftTeam = 'ult',
  userJoined = 'uj',
  userJoinedTeam = 'ujt',
  userJoinedConversation = 'ut',
  messageRemoved = 'rm',
  addedUserToTeam = 'added-user-to-team',
  removedUserFromTeam = 'removed-user-from-team',
  userMutedBy = 'user-muted',
  roleAdded = 'subscription-role-added',
}

export enum ChatMessageContentType {
  message = 'message',
  image = 'image',
  link = 'link',
}

export enum ChatMessageSendingStatus {
  sending = 'sending',
  failed = 'failed',
}
